import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { FxAlert, FxButton, FxIcon, FxSegmentedTabs, FxSpinner } from '@/components/fx'
import {
  Keys,
  reviewQueueItems,
  useReviewQueue,
  type KeyBinding,
  type ReviewQueueItem,
} from '@/components/review'
import { documentsApi, templatesApi } from '@/lib/api'
import { PageTitle, useShellHelp } from '@/layouts/shellChrome'
import type { EvaluationDocument } from '@/types/domain'

import { BulkHelp } from './bulk/BulkHelp'
import { BulkRow } from './bulk/BulkRow'
import { SavedToast } from './bulk/SavedToast'
import { ThresholdSlider } from './bulk/ThresholdSlider'
import {
  SORT_OPTIONS,
  TYPE_FILTERS,
  UNDO_MS,
  filterByType,
  previewImagePath,
  progressOf,
  shouldLoadMore,
  sortItems,
  typeCounts,
  type SavedWrite,
  type SortKey,
  type TypeFilter,
} from './bulk/bulkModel'

/**
 * Sammelprüfung — one field, read down a column, across every document that
 * flagged it.
 *
 * This is the screen that makes a stack of four hundred scans tractable. Its
 * whole design is one rhythm: look at the crop, agree, Enter, the next field
 * takes focus. Everything else on the page exists to keep that rhythm going —
 * the next page of documents is fetched before the list runs out, a saved row
 * stays where it is for the undo window instead of collapsing under the
 * cursor, and the page a crop came from opens *inside* the row rather than on
 * another screen.
 *
 * **The list does not move on its own.** The documents query is fetched once
 * and never re-fetched behind the reviewer's back: a list that reordered
 * itself mid-keystroke would post the wrong value the moment somebody's
 * muscle memory pressed Enter. The cost is that another reviewer's work is
 * not visible until "Liste aktualisieren" — which is stated on the page,
 * because the write is last-writer-wins and pretending otherwise would be
 * worse than saying so.
 */
const PAGE_SIZE = 25

export function BulkReviewPage() {
  const { templateId = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [params, setParams] = useSearchParams()

  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all')
  const [sort, setSort] = useState<SortKey>('confidence')
  const [threshold, setThreshold] = useState<number | null>(null)
  const [started, setStarted] = useState(false)
  const [saved, setSaved] = useState<SavedWrite | null>(null)
  /** Keys kept on screen although they are reviewed: the row somebody
   *  re-opened after saving it. */
  const [reopened, setReopened] = useState<string[]>([])

  const template = useQuery({
    // The same cache entry the cockpit fills, so arriving from its "Unklar"
    // card costs no second request.
    queryKey: ['template', templateId],
    queryFn: () => templatesApi.get(templateId),
    enabled: templateId !== '',
  })

  const counts = useQuery({
    queryKey: ['documents', 'counts', templateId],
    queryFn: () => documentsApi.counts(templateId),
    enabled: templateId !== '',
  })

  const documents = useInfiniteQuery({
    queryKey: ['review', 'bulk', templateId],
    queryFn: ({ pageParam }) =>
      documentsApi.list(templateId, { needsReview: true, page: pageParam, pageSize: PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page * last.pageSize < last.total ? last.page + 1 : undefined),
    enabled: templateId !== '',
    // See the note on the component: the queue must not move underneath the
    // person working through it.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  const docs: EvaluationDocument[] = useMemo(
    () => documents.data?.pages.flatMap((page) => page.docs) ?? [],
    [documents.data],
  )

  const pageByDocument = useMemo(() => {
    const map = new Map<string, string | null>()
    for (const doc of docs) map.set(doc.id, previewImagePath(doc))
    return map
  }, [docs])

  const effectiveThreshold = threshold ?? template.data?.confidenceThreshold ?? 0.8

  const allItems = useMemo(
    () => reviewQueueItems(docs, template.data, effectiveThreshold, { flaggedOnly: true }),
    [docs, template.data, effectiveThreshold],
  )

  const ordered = useMemo(
    () => sortItems(filterByType(allItems, typeFilter), sort),
    [allItems, typeFilter, sort],
  )

  const queue = useReviewQueue({
    templateId,
    items: ordered,
    threshold: effectiveThreshold,
  })

  // Counted over everything loaded, not over the filtered view: the tabs have
  // to say how much work each type still holds while one of them is open.
  // `queue.items` is the *filtered* source with the local writes folded in,
  // so the counts come from the unfiltered list run through the same fold —
  // otherwise selecting Text makes Checkbox and Signatur read (0) and the
  // headline says nothing is left while flagged fields are still waiting.
  // Destructured: `queue.applyOverrides` as a dependency reads to the lint
  // rule as a dependency on the whole `queue`, which changes every render.
  const { applyOverrides } = queue
  const allWithWrites = useMemo(() => applyOverrides(allItems), [applyOverrides, allItems])
  const openByType = useMemo(() => typeCounts(allWithWrites), [allWithWrites])
  const progress = useMemo(() => progressOf(allWithWrites), [allWithWrites])

  // -- Paging ahead of the cursor -----------------------------------------
  // On the primitives, not on `documents`: the query result is a new object
  // on every render, so depending on it ran this effect on every keystroke.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = documents
  useEffect(() => {
    if (
      shouldLoadMore(queue.open.length, {
        hasMore: hasNextPage === true,
        loading: isFetchingNextPage,
      })
    ) {
      void fetchNextPage()
    }
  }, [queue.open.length, hasNextPage, isFetchingNextPage, fetchNextPage])

  // -- Writes --------------------------------------------------------------
  const dirty = useRef(false)

  // What was cleared here changes the cockpit's counts and the document
  // list. Those caches are invalidated on the way out rather than per write,
  // so a refetch cannot pull rows out from under the person mid-rhythm.
  useEffect(
    () => () => {
      if (!dirty.current) return
      void queryClient.invalidateQueries({ queryKey: ['documents'] })
      void queryClient.invalidateQueries({ queryKey: ['template', templateId] })
    },
    [queryClient, templateId],
  )

  const submit = useCallback(
    async (item: ReviewQueueItem, value: string) => {
      setStarted(true)
      const previousValue = item.value
      const outcome = await queue.submitValue(value, item.key)
      if (!outcome.ok) return
      dirty.current = true
      setReopened((keys) => keys.filter((key) => key !== item.key))
      setSaved({
        key: item.key,
        label: item.label,
        previousValue,
        corrected: outcome.submission.kind === 'correct',
      })
    },
    [queue],
  )

  useEffect(() => {
    if (!saved) return
    const timer = window.setTimeout(() => setSaved(null), UNDO_MS)
    return () => window.clearTimeout(timer)
  }, [saved])

  const undo = useCallback(
    async (kind: 'repost' | 'reopen') => {
      if (!saved) return
      const item = queue.items.find((candidate) => candidate.key === saved.key)
      setSaved(null)
      if (!item) return
      // Either way the row comes back on screen: a review cannot be deleted,
      // so "undone" here means the value is right again and the row is open
      // for more work — not that the review never happened.
      setReopened((keys) => (keys.includes(item.key) ? keys : [...keys, item.key]))
      queue.focus(item.key)
      if (kind === 'repost') await queue.submitValue(saved.previousValue, item.key)
    },
    [queue, saved],
  )

  // -- Keyboard ------------------------------------------------------------
  const helpOpen = params.get('dialog') === 'hilfe'
  const setHelpOpen = useCallback(
    (open: boolean) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current)
          if (open) next.set('dialog', 'hilfe')
          else next.delete('dialog')
          return next
        },
        { replace: true },
      )
    },
    [setParams],
  )

  useShellHelp({ label: 'Hilfe zur Sammelprüfung', onOpen: () => setHelpOpen(true) })

  const bindings: KeyBinding[] = useMemo(
    () => [
      {
        id: 'next',
        // With a text input focused in every row, a bare arrow key belongs to
        // the caret. The modifier is what makes "skip this one" reachable
        // without leaving the field — `whenTyping`, because a chord with a
        // modifier can never be part of a word.
        keys: 'Mod+ArrowDown',
        label: 'Nächstes Feld (ohne zu speichern)',
        whenTyping: true,
        run: () => {
          setStarted(true)
          queue.next()
        },
      },
      {
        id: 'previous',
        keys: 'Mod+ArrowUp',
        label: 'Voriges Feld',
        whenTyping: true,
        run: () => {
          setStarted(true)
          queue.previous()
        },
      },
      {
        id: 'help',
        keys: 'Shift+?',
        label: 'Tastaturhilfe',
        run: () => setHelpOpen(true),
      },
      {
        id: 'back',
        keys: 'Escape',
        label: 'Zurück zum Formular',
        run: () => navigate(`/templates/${templateId}`),
      },
    ],
    [navigate, queue, setHelpOpen, templateId],
  )

  // -- Hand-off to the per-document screen ---------------------------------
  const firstVisible = queue.open[0]
  const walkThrough = () => {
    if (!firstVisible) return
    navigate(
      `/templates/${templateId}/dokumente/${firstVisible.documentId}?feld=${encodeURIComponent(firstVisible.fieldId)}`,
      // The order this screen is showing, handed on so the other screen walks
      // the same queue rather than inventing its own.
      { state: { queue: [...new Set(queue.open.map((item) => item.documentId))] } },
    )
  }

  const visible = queue.items.filter(
    (item) => !item.reviewed || item.key === saved?.key || reopened.includes(item.key),
  )

  const loading = template.isLoading || documents.isLoading
  const failed = template.isError || documents.isError

  return (
    <div className="flex flex-col gap-8 pb-24">
      <Keys bindings={bindings} enabled={!helpOpen} />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <Link to={`/templates/${templateId}`} className="flex items-center gap-2 text-primary-text">
          <FxIcon name="arrow_back" size={24} />
          Zurück
        </Link>
        <ThresholdSlider value={effectiveThreshold} onChange={setThreshold} />
      </div>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <PageTitle>Unklarheiten beheben</PageTitle>
          <p>
            {openByType.all === 1
              ? '1 Feld wartet auf Ihre Prüfung'
              : `${openByType.all} Felder warten auf Ihre Prüfung`}
            {docs.length > 0 && ` · ${docs.length} ${docs.length === 1 ? 'Dokument' : 'Dokumente'} geladen`}
            {counts.data?.needsReview != null && counts.data.needsReview > docs.length && (
              <> von {counts.data.needsReview}</>
            )}
          </p>
          <p className="text-small text-text-muted">
            Mit <kbd className="rounded-sm bg-fill px-1 font-semibold">Enter</kbd> im Feld übernehmen
            Sie den Wert und springen zum nächsten.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <FxButton
            variant="outlined"
            icon="refresh"
            onClick={() => {
              void documents.refetch()
              void counts.refetch()
            }}
            disabled={documents.isFetching}
          >
            Liste aktualisieren
          </FxButton>
          <FxButton variant="primary" onClick={walkThrough} disabled={!firstVisible}>
            Feld für Feld durchgehen
          </FxButton>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <FxSegmentedTabs
          aria-label="Feldtyp"
          items={TYPE_FILTERS.map((filter) => ({
            value: filter.value,
            label: `${filter.label} (${openByType[filter.value]})`,
          }))}
          active={typeFilter}
          onSelect={(value) => setTypeFilter(value as TypeFilter)}
        />
        <span className="grow" />
        <label htmlFor="bulk-sort" className="text-small">
          Sortierung
        </label>
        <select
          id="bulk-sort"
          value={sort}
          onChange={(event) => setSort(event.target.value as SortKey)}
          className="h-control rounded-md bg-surface px-4 shadow-raised"
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-4">
        <div
          className="h-1.5 grow rounded-full bg-fill"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={progress.total}
          aria-valuenow={progress.done}
          aria-label="Bearbeitete Felder"
        >
          <div className="h-1.5 rounded-full bg-success" style={{ width: `${progress.percent}%` }} />
        </div>
        <span className="whitespace-nowrap text-small">
          <b>{progress.done}</b> von {progress.total} erledigt
        </span>
      </div>

      {queue.error && (
        <FxAlert tone="danger" title="Nicht gespeichert">
          <div className="flex flex-wrap items-center gap-4">
            <span>
              „{queue.error.item.label}“ konnte nicht gespeichert werden. Der alte Wert steht wieder
              im Feld — bitte noch einmal versuchen.
            </span>
            <FxButton variant="outlined" onClick={queue.clearError}>
              Verstanden
            </FxButton>
          </div>
        </FxAlert>
      )}

      {loading && <FxSpinner label="Unklarheiten werden geladen …" />}

      {failed && !loading && (
        <FxAlert tone="danger" title="Liste konnte nicht geladen werden">
          Bitte laden Sie die Seite neu.
        </FxAlert>
      )}

      {!loading && !failed && visible.length === 0 && (
        <FxAlert tone="success" title="Keine offenen Unklarheiten">
          In den geladenen Dokumenten wartet kein Feld mehr auf eine Entscheidung.
        </FxAlert>
      )}

      <div className="flex flex-col gap-4">
        {visible.map((item) => (
          <BulkRow
            key={item.key}
            item={item}
            templateId={templateId}
            number={queue.items.indexOf(item) + 1}
            pageImagePath={pageByDocument.get(item.documentId) ?? null}
            active={started && queue.activeKey === item.key}
            busy={queue.isPending(item.key)}
            settled={item.reviewed}
            onFocus={() => {
              setStarted(true)
              queue.focus(item.key)
            }}
            onSubmit={(value) => void submit(item, value)}
          />
        ))}
      </div>

      {documents.hasNextPage && (
        <FxButton
          variant="tonal"
          onClick={() => void documents.fetchNextPage()}
          disabled={documents.isFetchingNextPage}
        >
          {documents.isFetchingNextPage ? 'Weitere Dokumente werden geladen …' : 'Weitere Dokumente laden'}
        </FxButton>
      )}

      {saved && <SavedToast write={saved} busy={queue.isPending(saved.key)} onAction={(kind) => void undo(kind)} />}

      {helpOpen && <BulkHelp bindings={bindings} onClose={() => setHelpOpen(false)} />}
    </div>
  )
}
